import type { TemplateData } from '@plone-collective/volto-editortemplates/types';
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  type PropsWithChildren,
  useEffect,
  useMemo,
} from 'react';
import {
  addBlockTemplate,
  deleteBlockTemplate,
  getBlockTemplate,
  getBlockTemplates,
  RESET_BLOCK_TEMPLATE_RESULTS,
  updateBlockTemplate,
} from '@plone-collective/volto-editortemplates/actions/blocks-templates';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useTypedSelector } from '@plone-collective/volto-editortemplates/helpers/helpers';
import type { BlockTemplatesState } from '@plone-collective/volto-editortemplates/reducers/blocks-reducers';
import { createPortal } from 'react-dom';
import Sidebar from '@plone/volto/components/manage/Sidebar/Sidebar';
import { useDebounceCallback, type DebouncedState } from 'usehooks-ts';
import { Toast } from '@plone/components';
import { defineMessages, useIntl } from 'react-intl';
import { toast } from 'react-toastify';

interface BlockContextType {
  blockTemplates: BlockTemplatesState;
  block: TemplateData;
  selectedBlock: string;
  setBlock: React.Dispatch<React.SetStateAction<TemplateData>>;
  setSelectedBlock: React.Dispatch<React.SetStateAction<string>>;
  onChange: (
    key: keyof TemplateData,
    value: TemplateData[keyof TemplateData],
  ) => void;
  save: () => void;
  add: () => void;
  deleteBlock: () => void;
  resetStore: () => void;
  getBlockByUID: (uid: TemplateData['uid']) => void;
  search: DebouncedState<(value: string) => void>;
}

const EMPTY_BLOCK_DATA: TemplateData = {
  '@type': 'blockTemplateSelector',
  name: '',
  config: {
    blocks: {},
    blocks_layout: { items: [] },
  },
  uid: '',
  date: '',
  id: '',
};

const messages = defineMessages({
  saveSuccessBlockTemplatesProvider: {
    id: 'saveSuccessBlockTemplatesProvider',
    defaultMessage: 'Template successfully saved.',
  },
  saveFailBlockTemplatesProvider: {
    id: 'saveFailBlockTemplatesProvider',
    defaultMessage: 'There was an error while saving this template.',
  },
  updateSuccessBlockTemplatesProvider: {
    id: 'updateSuccessBlockTemplatesProvider',
    defaultMessage: 'Template successfully updated.',
  },
  addFailBlockTemplatesProvider: {
    id: 'addFailBlockTemplatesProvider',
    defaultMessage: 'There was an error while setting up a new template.',
  },
  deleteSuccessBlockTemplatesProvider: {
    id: 'deleteSuccessBlockTemplatesProvider',
    defaultMessage: 'Template successfully deleted.',
  },
  deleteFailBlockTemplatesProvider: {
    id: 'deleteFailBlockTemplatesProvider',
    defaultMessage: 'There was an error while deleting this template.',
  },
});

const BlockContext = createContext<BlockContextType | undefined>(undefined);

const BlockTemplatesProvider = ({
  children,
  isEditMode,
}: PropsWithChildren<{ isEditMode?: boolean }>) => {
  const intl = useIntl();
  const [block, setBlock] = useState<TemplateData>(EMPTY_BLOCK_DATA);
  const [selectedBlock, setSelectedBlock] = useState<TemplateData['uid']>('');
  const dispatch = useDispatch();

  const blockTemplates = useTypedSelector((state) => state.blocksTemplates);
  useEffect(() => {
    if (isEditMode) dispatch(getBlockTemplates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedBlock &&
      blockTemplates?.templates?.some((t) => t.id === selectedBlock)
    )
      setBlock(blockTemplates?.templates?.find((t) => t.id === selectedBlock));
  }, [blockTemplates.templates, selectedBlock]);

  // Funzione per salvare il blocco
  const save = useCallback(async () => {
    // It just works https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/await.
    // Do not touch awaits!
    try {
      await dispatch(updateBlockTemplate(block));
      await dispatch(getBlockTemplates());
      toast.success(
        <Toast
          success
          title={intl.formatMessage({ id: 'Success' })}
          content={intl.formatMessage(
            messages.saveSuccessBlockTemplatesProvider,
          )}
        />,
      );
    } catch (e) {
      toast.error(
        <Toast
          error
          title={intl.formatMessage({ id: 'Error' })}
          content={intl.formatMessage(messages.saveFailBlockTemplatesProvider)}
        />,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block]);

  const add = async () => {
    const blockId = uuid();
    const newBlock = {
      ...EMPTY_BLOCK_DATA,
      id: blockId,
      date: new Date().toISOString(),
    };
    try {
      setBlock(newBlock);
      setSelectedBlock(blockId);
      // It just works https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/await.
      // Do not touch awaits!
      await dispatch(addBlockTemplate(newBlock));
      await dispatch(getBlockTemplates());
    } catch (e) {
      toast.error(
        <Toast
          error
          title={intl.formatMessage({ id: 'Error' })}
          content={intl.formatMessage(messages.addFailBlockTemplatesProvider)}
        />,
      );
    }
  };

  const deleteBlock = useCallback(async () => {
    try {
      // It just works https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/await.
      // Do not touch awaits!
      await dispatch(deleteBlockTemplate(block));
      await dispatch(getBlockTemplates());
      setBlock(null);
      setSelectedBlock('');
      toast.success(
        <Toast
          success
          title={intl.formatMessage({ id: 'Success' })}
          content={intl.formatMessage(
            messages.deleteSuccessBlockTemplatesProvider,
          )}
        />,
      );
    } catch (e) {
      toast.error(
        <Toast
          error
          title={intl.formatMessage({ id: 'Error' })}
          content={intl.formatMessage(
            messages.deleteFailBlockTemplatesProvider,
          )}
        />,
      );
    }
  }, [block]);

  const getBlockByUID = async (uid: TemplateData['uid']) => {
    try {
      await dispatch(getBlockTemplate(uid, uid));
    } catch (e) {
      console.error(e);
    }
  };

  const resetStore = async () => {
    await dispatch({
      type: RESET_BLOCK_TEMPLATE_RESULTS,
    });
  };

  const searchTemplates = (value: string) => {
    dispatch(getBlockTemplates(value));
  };

  const search = useDebounceCallback(searchTemplates, 500);

  const onChange = (
    key: keyof TemplateData,
    value: TemplateData[keyof TemplateData],
  ) => {
    setBlock((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const value = useMemo(
    () => ({
      block,
      setBlock,
      onChange,
      save,
      add,
      deleteBlock,
      blockTemplates,
      setSelectedBlock,
      getBlockByUID,
      resetStore,
      selectedBlock,
      search,
    }),
    [block, save, blockTemplates, selectedBlock, deleteBlock],
  );
  return (
    <BlockContext.Provider value={value}>
      {children}
      {selectedBlock &&
        createPortal(<Sidebar />, document.getElementById('sidebar'))}
    </BlockContext.Provider>
  );
};

const useBlockTemplates = (): BlockContextType => {
  const context = useContext(BlockContext);
  if (!context) {
    throw new Error(
      'useBlockTemplates must be used within a BlockTemplatesProvider',
    );
  }
  return context;
};

export { BlockTemplatesProvider, useBlockTemplates };
